import {
  Box,
  List,
  Popover,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { getWalletIcon, useBtcConnection, useConnection } from 'modules/auth';
import { SNACKBAR_ZINDEX } from 'modules/common/const';
import { useBreakpoints } from 'modules/common/hooks/useBreakpoints';
import { useCopyClick } from 'modules/common/hooks/useCopyClick';
import { CopyIcon, LeaveIcon } from 'modules/common/icons';
import { useTranslation } from 'modules/i18n';
import CopyToClipboard from 'react-copy-to-clipboard';
import { WalletDropdownListItem } from './components/WalletDropdownListItem/WalletDropdownListItem';
import { translation } from './translation';
import { useMemo } from 'react';
import { getShortAddr } from 'modules/common/utils/getShortAddr';
import { OLstToken } from 'modules/api';
import { LbtcBalance } from 'modules/common/components/LbtcBalance';

interface IWalletDropdown {
  id: string;
  anchorEl: HTMLButtonElement | null;
  handleClose: () => void;
}

const PaperPropsSxMobile: SxProps<Theme> = {
  width: '100%',
  maxWidth: 'none',
  left: '0 !important',
  right: 0,
  p: 0,
  mt: 1,
  border: 'none',
  boxShadow: 'none',
};

export const WalletDropdown = ({
  id,
  anchorEl,
  handleClose,
}: IWalletDropdown) => {
  const open = Boolean(anchorEl);
  const { keys, t } = useTranslation(translation);
  const { isConnected, walletId, address = '', disconnect } = useConnection();
  const { disconnect: btcDisconnect, isConnected: isBtcConnected } =
    useBtcConnection();
  const { handleCopy } = useCopyClick();
  const { isSmDown } = useBreakpoints();
  const shortAddress = useMemo(() => getShortAddr(address, 4), [address]);

  const handleDisconnectClick = () => {
    if (isBtcConnected) {
      btcDisconnect();
    }
    disconnect();
  };

  if (!isConnected) {
    return null;
  }

  return (
    <Popover
      id={id}
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      sx={{
        zIndex: SNACKBAR_ZINDEX + 1,
      }}
      PaperProps={{
        sx: isSmDown
          ? PaperPropsSxMobile
          : {
              width: 420,
              p: 0,
              mt: 1,
              borderRadius: 1.2,
              border: '1px solid',
              borderColor: 'grey.50',
            },
      }}
    >
      <Stack
        gap={2}
        sx={{
          minWidth: 300,
          p: theme => ({
            xs: theme.spacing(2.5, 3),
            sm: 2.5,
          }),
        }}
      >
        <Stack gap={1}>
          <Box component={getWalletIcon(walletId)} height={28} width={28} />
          <Typography variant="h6">{shortAddress}</Typography>
          <LbtcBalance showLbtc={false} valueToken={OLstToken.LBTC} />
        </Stack>

        <List sx={{ p: 0 }}>
          <CopyToClipboard text={address} onCopy={handleCopy}>
            <WalletDropdownListItem
              icon={CopyIcon}
              iconProps={{
                sx: {
                  color: 'text.secondary',
                },
              }}
              label={t(keys.copyAddress)}
              onClick={() => {
                handleClose();
              }}
            />
          </CopyToClipboard>
          <WalletDropdownListItem
            icon={LeaveIcon}
            label={t(keys.disconnectWallet)}
            iconProps={{
              sx: {
                color: 'text.secondary',
              },
            }}
            onClick={() => {
              handleDisconnectClick();
              handleClose();
            }}
          />
        </List>
      </Stack>
    </Popover>
  );
};
